
export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
}

/**
 * Safely updates document head SEO tags.
 * Silently fails when executed in non‑browser environments.
 */
export const updateSEO = (seoData: SEOData): void => {
  if (typeof document === 'undefined') return;

  // Update title
  if (seoData.title) {
    document.title = seoData.title;
  }

  // Update meta description
  if (seoData.description) {
    updateMetaTag('description', seoData.description);
  }

  // Update keywords if provided
  if (seoData.keywords) {
    updateMetaTag('keywords', seoData.keywords);
  }

  // Update canonical URL
  const canonicalHref = seoData.canonical ?? window.location.pathname;
  updateLinkTag('canonical', canonicalHref);

  // Update Open Graph tags
  updateMetaProperty('og:title', seoData.ogTitle ?? seoData.title);
  updateMetaProperty('og:description', seoData.ogDescription ?? seoData.description);
  updateMetaProperty('og:type', seoData.ogType ?? 'website');
  updateMetaProperty('og:url', window.location.href);

  // Update Twitter Card tags
  updateMetaTag('twitter:title', seoData.twitterTitle ?? seoData.title);
  updateMetaTag('twitter:description', seoData.twitterDescription ?? seoData.description);
};

/**
 * Creates or updates a <meta name="..."> tag.
 */
const updateMetaTag = (name: string, content: string): void => {
  if (!content) return;
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
};

/**
 * Creates or updates a <meta property="..."> tag (used for Open Graph).
 */
const updateMetaProperty = (property: string, content: string): void => {
  if (!content) return;
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
};

/**
 * Creates or updates a <link rel="..."> tag (used for canonical URLs).
 */
const updateLinkTag = (rel: string, href: string): void => {
  if (!href) return;
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
};

/**
 * Injects a JSON‑LD script tag into the document head.
 * Errors are caught so they do not break the rest of the page.
 */
export const addJsonLd = (data: any): void => {
  if (typeof document === 'undefined' || !data) return;
  try {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to add JSON‑LD:', err);
  }
};
